import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect } from 'react';
import {
  getFirebaseAuth,
  onAuthStateChanged,
  apiFetch,
  listResource,
} from '@diggens/api-client';
import type { User, UserPermissions, UserModule, SystemPermissions } from '../types';

interface AuthStore {
  // State
  user: User | null;
  users: User[] | null;
  permissions: UserPermissions | null;
  modules: UserModule[] | null;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setUsers: (users: User[] | null) => void;
  setPermissions: (permissions: UserPermissions | null) => void;
  setModules: (modules: UserModule[] | null) => void;
  fetchUsers: (tenantId: string) => Promise<void>;
  updateUserProfile: (userId: string, updates: Partial<User>) => Promise<void>;

  // Helpers
  hasPermission: (path: string) => boolean;
  hasModule: (moduleId: string, minAccess?: 'read' | 'write' | 'full') => boolean;
  hasSystemPermission: (permission: keyof SystemPermissions) => boolean;

  // Lifecycle
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      users: null,
      permissions: null,
      modules: null,
      isLoading: false,

      // Setters
      setUser: (user) => set({ user }),
      setUsers: (users) => set({ users }),
      setPermissions: (permissions) => set({ permissions }),
      setModules: (modules) => set({ modules }),

      // Fetch all users for tenant
      fetchUsers: async (tenantId) => {
        const { data } = await listResource<User>('users', {
          tenant_id: tenantId,
          order: 'created_at.desc',
        });
        set({ users: data });
      },

      // Update user profile
      updateUserProfile: async (userId, updates) => {
        const data = await apiFetch<User>(`/resource/users/${encodeURIComponent(userId)}`, {
          method: 'PATCH',
          body: JSON.stringify(updates),
        });

        // Update current user if it's the same
        const { user, users } = get();
        if (user?.id === userId) {
          set({ user: data });
        }

        // Update in users array
        if (users) {
          set({
            users: users.map((u) => (u.id === userId ? data : u)),
          });
        }
      },

      // Permission checkers
      hasPermission: (path: string) => {
        const { permissions } = get();
        if (!permissions) return false;

        const parts = path.split('.');
        let value: any = permissions;

        for (const part of parts) {
          value = value?.[part];
          if (value === undefined) return false;
        }

        return Boolean(value);
      },

      hasModule: (moduleId: string, minAccess = 'read') => {
        const { modules } = get();
        if (!modules) return false;

        const module = modules.find((m) => m.module_id === moduleId);
        if (!module) return false;

        const accessLevels = ['none', 'read', 'write', 'full'];
        const userLevel = accessLevels.indexOf(module.access_level);
        const requiredLevel = accessLevels.indexOf(minAccess);

        return userLevel >= requiredLevel;
      },

      hasSystemPermission: (permission) => {
        const { permissions } = get();
        return permissions?.system?.[permission] ?? false;
      },

      // Initialize on app load
      initialize: async () => {
        set({ isLoading: true });

        try {
          const auth = getFirebaseAuth();
          const authUser = auth.currentUser;

          if (!authUser) {
            get().clear();
            return;
          }

          // Load current user profile (no tenant scoping — works for super_admins)
          const userData = await apiFetch<any>('/auth/me');
          if (!userData) {
            console.error('AuthStore: No user found for firebase_uid');
            get().clear();
            return;
          }

          set({ user: userData });

          // Load permissions and modules
          const perms = await apiFetch<UserPermissions>('/users/' + encodeURIComponent(userData.id) + '/permissions');
          set({ permissions: perms });

          const mods = await apiFetch<UserModule[]>('/users/' + encodeURIComponent(userData.id) + '/modules');
          set({ modules: mods });
        } catch (error) {
          console.error('AuthStore: Failed to initialize:', error);
          get().clear();
        } finally {
          set({ isLoading: false });
        }
      },

      // Refresh permissions
      refresh: async () => {
        const { user } = get();
        if (!user) return;

        try {
          const perms = await apiFetch<UserPermissions>('/users/' + encodeURIComponent(user.id) + '/permissions');
          const mods = await apiFetch<UserModule[]>('/users/' + encodeURIComponent(user.id) + '/modules');
          set({ permissions: perms, modules: mods });
        } catch (error) {
          console.error('Failed to refresh permissions:', error);
        }
      },

      // Clear on logout
      clear: () =>
        set({
          user: null,
          permissions: null,
          modules: null,
        }),
    }),
    {
      name: 'diggens-auth-storage',
      partialize: (state) => ({
        user: state.user,
        permissions: state.permissions,
        modules: state.modules,
      }),
    }
  )
);

// Hook to initialize auth on app mount
export function useInitAuth() {
  useEffect(() => {
    const initialize = useAuthStore.getState().initialize;
    const clear = useAuthStore.getState().clear;

    initialize();

    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged((user) => {
      if (!user) {
        clear();
      }
    });

    return () => unsubscribe();
  }, []);
}
